import React from "react";
import { motion } from "framer-motion";
import { TableCell } from "./TableV2/Table";
import { Eye, EyeOff, Instagram, MapPin, Globe } from "lucide-react";
import Tooltip from "./Tooltip/Tooltip";
import { ButtonV2 } from "./ButtonV2/ButtonV2";
import { Badge } from "./Badge/Badge";
import { Partner } from "../types/partner";
import RevealAnimation from "./RevealAnimation";
import { useTranslation } from "react-i18next";

interface PartnerTableRowProps {
  partner: Partner;
  expandedPartner: number | null;
  onExpand: (id: number) => void;
  onReveal: (partner: Partner) => void;
  isRevealed: boolean;
  formatFollowers: (count: number | null) => string;
  getQualityRatingColor: (rating: number | undefined) => string;
  getQualityRatingText: (rating: number | undefined) => string;
  calculateQualityRating: (partner: Partner) => number;
  getHostname: (url?: string | null) => string;
  renderExpandedRow?: React.ReactNode;
}

const PartnerTableRow: React.FC<PartnerTableRowProps> = ({
  partner,
  expandedPartner,
  onExpand,
  onReveal,
  isRevealed,
  formatFollowers,
  getQualityRatingColor,
  getQualityRatingText,
  calculateQualityRating,
  getHostname,
  renderExpandedRow,
}) => {

  const { t } = useTranslation();

  return (
    <>
      <motion.tr
        className="cursor-pointer hover:bg-muted/50"
        initial={false}
        animate={isRevealed ? "revealed" : "hidden"}
        variants={{
          revealed: {
            background: "rgba(255, 255, 255, 0)",
            transition: { duration: 0.5, ease: "easeOut" },
          },
          hidden: {
            background: "rgba(255, 255, 255, 0)",
            transition: { duration: 0.3 },
          },
        }}
      >
        {/* Name/expand cell */}
        <TableCell className="font-medium">
          <RevealAnimation isRevealed={isRevealed}>
            <div className="flex items-center space-x-2">
              <div onClick={() => onExpand(partner.id)} className="flex items-center">
                <span className="mr-2">
                  {expandedPartner === partner.id ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                  )}
                </span>
                {isRevealed ? (
                  <>
                    {partner.businessName}
                    {partner.featured && (
                      <Badge variant="secondary" className="ml-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
                        Featured
                      </Badge>
                    )}
                    {partner.followers && partner.followers > 1000 && partner.partnersWith && partner.partnersWith.length > 0 && (
                      <Tooltip content={getQualityRatingText(calculateQualityRating(partner))}>
                        <span
                          className={`ml-2 w-3 h-3 inline-block rounded-full align-middle ${getQualityRatingColor(calculateQualityRating(partner))}`}
                          style={{ verticalAlign: 'middle' }}
                        />
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground">{t("partners.find.hidden_partner", "Hidden Partner")}</span>
                )}
              </div>
            </div>
          </RevealAnimation>
        </TableCell>
        {/* Social cell */}
        <TableCell>
          <RevealAnimation isRevealed={isRevealed}>
            {isRevealed ? (
              <div className="space-y-1">
                {/* Instagram */}
                {partner.instagram ? (
                  <a
                    href={`https://instagram.com/${partner.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-primary hover:underline"
                  >
                    <Instagram className="h-4 w-4" />
                    <span>@{partner.instagram}</span>
                    <span className="text-xs text-muted-foreground">
                      ({formatFollowers(partner.followers)})
                    </span>
                  </a>
                ) : (
                  <div className="flex items-center space-x-1 text-muted-foreground">
                    <Instagram className="h-4 w-4" />
                    <span>N/A</span>
                  </div>
                )}
                {/* TikTok */}
                <a
                  href={partner.tiktok ? `https://tiktok.com/@${partner.tiktok}` : "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center space-x-1 ${partner.tiktok ? "text-primary hover:underline" : "text-muted-foreground"}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                  <span>{partner.tiktok ? `@${partner.tiktok}` : "N/A"}</span>
                  <span className="text-xs text-muted-foreground">
                    {partner.tiktokFollowers ? `(${formatFollowers(partner.tiktokFollowers)})` : ""}
                  </span>
                </a>
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-muted-foreground">
                <EyeOff className="h-4 w-4" />
                <span>{formatFollowers(partner.followers)} followers</span>
              </div>
            )}
          </RevealAnimation>
        </TableCell>
        {/* Contact cell */}
        <TableCell>
          <RevealAnimation isRevealed={isRevealed}>
            {isRevealed ? (
              <div className="space-y-1">
                <div className="flex items-center space-x-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-muted-foreground">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <span>{partner.phone || "Unknown"}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-muted-foreground">
                    <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                  </svg>
                  <span className="text-primary hover:underline">
                    {partner.email || "N/A"}
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <EyeOff className="h-4 w-4" />
                  <span>{t("partners.find.hidden_phone", "Phone hidden")}</span>
                </div>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <EyeOff className="h-4 w-4" />
                  <span>{t("partners.find.hidden_email", "Email hidden")}</span>
                </div>
              </div>
            )}
          </RevealAnimation>
        </TableCell>
        {/* Location cell */}
        <TableCell>
          <RevealAnimation isRevealed={isRevealed}>
            <div className="flex items-center space-x-1">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{partner.location}, {partner.country}</span>
            </div>
            <div className="flex items-center space-x-1 mt-1">
              <Globe className="h-4 w-4 text-muted-foreground" />
              {partner.website && !/^[•.]+$/.test(partner.website) ? (
                <a
                  href={partner.website.match(/^https?:\/\//) ? partner.website : `https://${partner.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {getHostname(partner.website)}
                </a>
              ) : (
                <span className="text-muted-foreground">
                  {getHostname(partner.website)}
                </span>
              )}
            </div>
          </RevealAnimation>
        </TableCell>
        {/* Partnerships cell */}
        <TableCell>
          <RevealAnimation isRevealed={isRevealed}>
            {isRevealed ? (
              <div className="relative group">
                <div className="flex items-center space-x-1 cursor-pointer">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-muted-foreground">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span>
                    {partner.partnersWith ? partner.partnersWith.length : 0}
                  </span>
                </div>
                {partner.partnersWith && partner.partnersWith.length > 0 && (
                  <div className="absolute z-10 invisible group-hover:visible bg-white dark:bg-gray-800 shadow-lg rounded-md p-3 min-w-[220px] mt-1 left-0 border border-gray-200 dark:border-gray-700">
                    <h4 className="font-medium mb-2 text-sm text-gray-900 dark:text-gray-100">
                      {t("partners.find.partners", "Partners")}:
                    </h4>
                    <div className="space-y-1">
                      {partner.partnersWith.map((p, idx) => (
                        <div key={idx} className="text-sm py-0.5 px-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">{p}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-muted-foreground">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-muted-foreground">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span>{partner.partnersWith ? partner.partnersWith.length : 0} {t("partners.find.partners", "partners")}</span>
              </div>
            )}
          </RevealAnimation>
        </TableCell>
        {/* Actions cell */}
        <TableCell className="text-right min-w-[120px]">
          {!isRevealed && (
            <Tooltip content={t("partners.find.reveal_information", "Reveal information")}>
              <ButtonV2
                variant="ghost"
                size="icon"
                onClick={() => onReveal(partner)}
                className="bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-400 text-white transition-all duration-300 border-0 outline-none ring-2 ring-pink-200/40 focus:ring-4 focus:ring-pink-300/60"
              >
                <Eye className="h-5 w-5" />
              </ButtonV2>
            </Tooltip>
          )}
        </TableCell>
      </motion.tr>
      {renderExpandedRow}
    </>
  );
};

export default PartnerTableRow; 